// 3h · Compliance Proof — reusable Owner/Admin-only button.
// POSTs to /api/records/{record_type}/{record_id}/compliance-proof and shows
// a modal with the signed PDF download link + a Copy-verifier-link action.
// Hidden entirely for non-Owner/Admin users. Regenerate calls the endpoint
// again (each generation is server-audited).
import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isOwnerOrAdmin(user) {
  const rk = user?.role_key;
  return rk === "OWNER_SUPER_ADMIN" || rk === "ADMIN";
}

export default function ComplianceProofButton({ recordType, recordId, label = "Compliance Proof" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [proof, setProof] = useState(null);
  const [open, setOpen] = useState(false);

  if (!isOwnerOrAdmin(user)) return null;

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/records/${recordType}/${recordId}/compliance-proof`,
        {},
        { withCredentials: true }
      );
      setProof(r.data);
      setOpen(true);
      toast.success("Compliance Proof generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Failed to generate proof");
    } finally { setBusy(false); }
  };

  const copyVerifierLink = async () => {
    if (!proof) return;
    const url = `${window.location.origin}${API}/compliance-proof/verify/${proof.token}`.replace("/api/api/", "/api/");
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Verifier link copied");
    } catch { toast.error("Copy failed — long-press to copy manually"); }
  };

  const downloadUrl = proof
    ? `${process.env.REACT_APP_BACKEND_URL}${proof.signed_url}`
    : null;

  return (
    <>
      <button
        data-testid={`compliance-proof-btn-${recordType}`}
        onClick={generate} disabled={busy}
        style={{
          padding: "6px 12px", borderRadius: 4, cursor: busy ? "wait" : "pointer",
          background: "#12233F", color: "#F5F1E8",
          border: "2px solid #C6A15B", fontSize: 11,
          letterSpacing: 1, textTransform: "uppercase",
          fontFamily: "'Space Mono', monospace",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "Generating…" : label}
      </button>
      {open && proof && (
        <div role="dialog" data-testid="compliance-proof-dialog"
             style={{ position: "fixed", inset: 0, background: "#0006",
                      display: "grid", placeItems: "center", zIndex: 300 }}>
          <div style={{ width: 560, maxWidth: "94vw", background: "white",
                        borderRadius: 6, padding: 24, borderTop: "4px solid #C6A15B" }}>
            <h2 style={{ margin: 0, fontFamily: "Playfair Display, serif", color: "#12233F" }}>
              Compliance Proof — {recordType} · {String(recordId).slice(0, 8)}
            </h2>
            <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginTop: 4 }}>
              Signed · {proof.attachments} attachments · {proof.audit_entries} audit entries · chain idx {proof.hash_chain_index_at_generation}
            </p>
            <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
              <a href={downloadUrl} target="_blank" rel="noreferrer"
                  data-testid="compliance-proof-download"
                  style={{ padding: "10px 14px", background: "#12233F", color: "#F5F1E8",
                           border: "2px solid #C6A15B", borderRadius: 4,
                           textDecoration: "none", textAlign: "center",
                           fontFamily: "'Space Mono', monospace", fontSize: 12,
                           letterSpacing: 1, textTransform: "uppercase" }}>
                Download PDF
              </a>
              <button onClick={copyVerifierLink} data-testid="compliance-proof-copy-verifier"
                      style={{ padding: "10px 14px", background: "white", color: "#12233F",
                               border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer",
                               fontSize: 12 }}>
                Copy verifier link
              </button>
              <p style={{ fontSize: 11, color: "#64748b", margin: 0, lineHeight: 1.5 }}>
                The verifier link lets an insurer or surety confirm this record's
                audit chain hasn't drifted since the proof was generated. Public
                verifier mode returns a redacted result — no data leaks.
              </p>
            </div>
            <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", gap: 10 }}>
              <button data-testid="compliance-proof-close" onClick={() => setOpen(false)}
                      style={{ padding: "8px 14px", background: "white", color: "#12233F",
                               border: "1px solid #cbd5e1", borderRadius: 4, cursor: "pointer" }}>
                Close
              </button>
              <button data-testid="compliance-proof-regenerate" onClick={generate} disabled={busy}
                      style={{ padding: "8px 14px", background: "#F5F1E8", color: "#12233F",
                               border: "1px solid #C6A15B", borderRadius: 4,
                               cursor: busy ? "wait" : "pointer", fontSize: 12 }}>
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
